import ClassTeachingDetails from "@/components/ClassTeachingDetails";
/**
 * @fileoverview Lesson Summary ("Studio Quiet") — a document-style reading view:
 * a wide serif prose column (summary + practice + requests + transcript) and a
 * metadata side rail (email recipients, key elements, core concepts, assignment
 * notes). All data
 * flow, polling, PDF export, send-email, and testids are preserved.
 */

import { useState, useEffect, useRef, useCallback, useContext } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "@/App";
import SessionBrief from "@/components/SessionBrief";
import { Loader2, ToggleLeft, ToggleRight, CheckCircle2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import axios from "axios";
import api, { getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { exportLessonPdf } from "@/lib/exportLessonPdf";
import { SCHEDULER_VIEW_ENABLED, rehearsalsEnabled, lessonAttachmentsEnabled } from "@/lib/featureFlags";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_HINT,
  MAX_ATTACHMENTS_PER_LESSON,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  formatBytes,
  resolveAttachmentType,
  totalAttachmentBytes,
  validateAttachment,
} from "@/lib/attachments";
import { formatRecipientList, summaryRecipients } from "@/lib/consent";
import { Card, Button, Eyebrow, Icon, Tag } from "@/components/studio/primitives";
import LessonWorkspaceStatus from "@/components/studio/LessonWorkspaceStatus";
import {
  EditableList, FIELD_CLS, cleanList, listProps, toListItems,
} from "@/components/studio/EditableList";
import ProgramChip from "@/components/ProgramChip";
import LessonClips from "@/components/LessonClips";
import CommentThread from "@/components/CommentThread";
import RichText from "@/components/RichText";
import RichTextEditor from "@/components/studio/RichTextEditor";
import { isInstructorWritten } from "@/lib/lessonAuthorship";
import { sectionLabel } from "@/lib/lessonSections";

// Why a minor's own address is not on the summary. The backend
// (`_summary_recipients`) is the authority; `summaryRecipients` mirrors it.
const STUDENT_EXCLUDED_NOTE = {
  under_13: "Not emailed — under-13 summaries go to the parent only",
  "13_17": "Not copied — their parent didn't opt in",
};

// The preview frame shows the REAL email, whose own background is white. This
// is the one surface where the app's dark tokens would be a lie about what the
// family receives, so the raw color is deliberate.
const PREVIEW_FRAME_CLS = "h-[52vh] w-full rounded-[12px] border border-[var(--studio-divider)] bg-white"; // brand-lint-ignore

const LessonSummary = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isClass = searchParams.get("kind") === "class";
  const viewOnlyParam = searchParams.get("readOnly") === "1";
  const { user } = useContext(AuthContext) || {};
  // The "Assign Rehearsal" toggle's only consumer is the student portal's
  // Rehearse CTA, which is itself gated by rehearsalsEnabled — so when the
  // rehearsal kill switch is off the toggle does nothing a student can act on.
  // Hide it (default-on when the flag field is absent) to match every other
  // rehearsal surface; the instructor notes below are unrelated and stay.
  const showRehearsalToggle = rehearsalsEnabled(user);
  const [lesson, setLesson] = useState(null);
  /* A class can be taught by two instructors, and its lessons are shared: both
     of them read the whole history. Writing over a colleague's write-up is a
     different act from reading it, and stays with whoever wrote it — so their
     lesson opens with the editing put away rather than with buttons the server
     will refuse. */
  const readOnly = viewOnlyParam || Boolean(lesson?.user_id && user?.user_id && lesson.user_id !== user.user_id);
  /* A substitute's lesson (docs/substitute-requests.md) is the organization's
     to send, whoever wrote it: its author may correct it, nobody here shares it. */
  const substituteLesson = Boolean(lesson?.substitute);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [instructorNotes, setInstructorNotes] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [rehearsalAssigned, setRehearsalAssigned] = useState(false);
  const [savingToggle, setSavingToggle] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  /* Practice recordings and "Assign practice" live on the student's page
     (B5): a rehearsal and an assignment describe the student's week rather
     than this one lesson, so the lesson page no longer fetches either. */
  // Lesson content editing (parity with the mobile LessonSummaryScreen): one
  // toggle puts the whole document into edit mode rather than per-field editors.
  const [editing, setEditing] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [titleText, setTitleText] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const [studentFocus, setStudentFocus] = useState([]);
  const [keyElements, setKeyElements] = useState([]);
  const [practiceItems, setPracticeItems] = useState([]);
  const [coreConcepts, setCoreConcepts] = useState([]);
  const [studentRequests, setStudentRequests] = useState([]);
  const [instructorWritten, setInstructorWritten] = useState(false);
  const [pendingNav, setPendingNav] = useState(null);
  // Exact HTML of the summary email, fetched on demand so an instructor can
  // see what a family receives before (and after) sending it.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);
  // Instructor-attached files. Kept in their own state slice rather than read
  // off `lesson`, because uploads commit immediately and must survive both the
  // edit toggle and the 5s poll.
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(null); // {name, percent}
  const attachmentInputRef = useRef(null);
  const showAttachments = lessonAttachmentsEnabled(user);
  const lessonRef = useRef(null);
  const isFetchingRef = useRef(false);
  const abortRef = useRef(null);
  const sendingRef = useRef(false);
  // Read by the poll so an in-flight refetch can't reseed fields mid-edit.
  const editingRef = useRef(false);
  editingRef.current = editing;

  /** Seed every editable field from a freshly-fetched lesson. */
  const initEditState = useCallback((data) => {
    setTitleText(data.title || "");
    setSummaryText(data.summary || "");
    setStudentFocus(toListItems(data.student_focus));
    setKeyElements(toListItems(data.key_elements));
    setPracticeItems(toListItems(data.practice_items));
    setCoreConcepts(toListItems(data.core_concepts));
    setStudentRequests(toListItems(data.student_requests));
    setInstructorNotes(data.instructor_notes || "");
    setRehearsalAssigned(data.rehearsal_assigned || false);
    setInstructorWritten(isInstructorWritten(data));
  }, []);

  const fetchLessonData = useCallback(async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      const signal = abortRef.current?.signal;
      const recRes = await api.get(isClass ? `/class-lessons/${lessonId}` : `/lessons/${lessonId}`, { signal });
      setLesson(recRes.data);
      lessonRef.current = recRes.data;
      // Seeded OUTSIDE the editing guard below: that guard protects text the
      // instructor is typing, and attachments are never typed — the server is
      // always right about them, and their presigned URLs need refreshing.
      setAttachments(recRes.data.attachments || []);
      // The lesson polls every 5s while processing; reseeding the inputs from a
      // poll response would overwrite whatever the instructor is typing.
      if (!editingRef.current) initEditState(recRes.data);
      if (!isClass && recRes.data.student_id) {
        const studentRes = await api.get(`/students/${recRes.data.student_id}`, { signal });
        setStudent(studentRes.data);
      }
    } catch (_error) {
      if (!abortRef.current?.signal.aborted) {
        toast.error("Failed to load");
        navigate("/dashboard");
      }
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [lessonId, navigate, isClass, initEditState]);

  useEffect(() => {
    abortRef.current = new AbortController();
    fetchLessonData();
    const TERMINAL = new Set(["complete", "failed", "processing_unknown"]);
    const MAX_POLL_MS = 5 * 60 * 1000;
    const startedAt = Date.now();
    const interval = setInterval(() => {
      const current = lessonRef.current;
      if (TERMINAL.has(current?.processing_status) || Date.now() - startedAt > MAX_POLL_MS) {
        clearInterval(interval);
        return;
      }
      fetchLessonData();
    }, 5000);
    return () => {
      clearInterval(interval);
      abortRef.current?.abort();
    };
  }, [lessonId, fetchLessonData]);

  const handleSaveNotes = async () => {
    setIsSaving(true);
    try {
      await api.put(isClass ? `/class-lessons/${lessonId}` : `/lessons/${lessonId}`, { instructor_notes: instructorNotes });
      toast.success("Saved");
    } catch (_error) { toast.error("Failed"); }
    finally { setIsSaving(false); }
  };

  const lessonEndpoint = isClass ? `/class-lessons/${lessonId}` : `/lessons/${lessonId}`;

  const editedPayload = () => ({
    // The backend requires a non-empty title, so a cleared field keeps the
    // existing one rather than failing the whole save. An org-scheduled
    // session's name is the org's (decision D7): it rides along unchanged.
    title: lesson?.title_locked ? lesson?.title || "" : titleText.trim() || lesson?.title || "",
    summary: summaryText,
    student_focus: cleanList(studentFocus),
    key_elements: cleanList(keyElements),
    practice_items: cleanList(practiceItems),
    core_concepts: cleanList(coreConcepts),
    student_requests: cleanList(studentRequests),
    instructor_notes: instructorNotes,
    instructor_written: instructorWritten,
  });

  const isDirty =
    editing && lesson && JSON.stringify(editedPayload()) !== JSON.stringify({
      title: lesson.title || "",
      summary: lesson.summary || "",
      student_focus: lesson.student_focus || [],
      key_elements: lesson.key_elements || [],
      practice_items: lesson.practice_items || [],
      core_concepts: lesson.core_concepts || [],
      student_requests: lesson.student_requests || [],
      instructor_notes: lesson.instructor_notes || "",
      instructor_written: isInstructorWritten(lesson),
    });

  useEffect(() => {
    if (!isDirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [isDirty]);

  const guardedNavigate = (to) => {
    if (isDirty) setPendingNav(to);
    else navigate(to);
  };

  /**
   * Persist every edited field in one PUT. Returns true on success so callers
   * that must flush first (send-email) can abort when the save fails.
   */
  const handleSaveEdit = async () => {
    setSavingEdit(true);
    try {
      const updates = editedPayload();
      await api.put(lessonEndpoint, updates);
      // The endpoint returns {message}, not the lesson — apply locally so the
      // reading view reflects the edit without waiting for a refetch.
      const next = { ...lesson, ...updates };
      setLesson(next);
      lessonRef.current = next;
      initEditState(next);
      setEditing(false);
      toast.success("Lesson updated");
      return true;
    } catch (error) {
      // Stay in edit mode — bailing out here would discard the instructor's work.
      toast.error(getErrorMessage(error, "Failed to save changes"));
      return false;
    } finally {
      setSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    initEditState(lesson);
    setEditing(false);
  };

  // --- Attachments -------------------------------------------------------
  // Uploads commit IMMEDIATELY rather than on Save. The bytes are in S3 before
  // the UI knows anything, so a "cancel" couldn't roll them back; staging File
  // objects across the edit toggle would mean holding blobs in memory and
  // re-implementing dirty tracking for binaries; and handleSendEmail flushes
  // pending edits before sending, so with immediate commit there is nothing
  // left to flush.
  const attachmentBase = isClass ? `/class-lessons/${lessonId}` : `/lessons/${lessonId}`;
  const attachedBytes = totalAttachmentBytes(attachments);
  const canAttach =
    !readOnly && showAttachments && attachments.length < MAX_ATTACHMENTS_PER_LESSON;

  const uploadAttachment = async (file) => {
    if (!file) return;
    const problem = validateAttachment(file, attachments);
    if (problem) { toast.error(problem); return; }
    // The resolved type (not file.type) rides both the mint and the S3 PUT:
    // iOS hands back an empty File.type for some picks, and the presigned URL
    // signs whatever we declared here.
    const contentType = resolveAttachmentType(file);

    setUploading({ name: file.name, percent: 0 });
    try {
      const { data: mint } = await api.post(`${attachmentBase}/attachments`, {
        filename: file.name,
        content_type: contentType,
        size_bytes: file.size,
      });
      // Bare axios: the presigned S3 URL must not get the API client's auth
      // headers, and Content-Type must match what the backend signed.
      await axios.put(mint.upload_url, file, {
        headers: { "Content-Type": contentType },
        onUploadProgress: (e) => {
          if (!e.total) return;
          setUploading({ name: file.name, percent: Math.round((e.loaded / e.total) * 100) });
        },
      });
      const { data } = await api.post(
        `${attachmentBase}/attachments/${mint.attachment_id}/confirm`,
        { filename: file.name, content_type: contentType },
      );
      setAttachments(data.attachments || []);
      toast.success("Attached");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not attach that file"));
    } finally {
      setUploading(null);
      // Reset so the same file can be picked again after a failure.
      if (attachmentInputRef.current) attachmentInputRef.current.value = "";
    }
  };

  const removeAttachment = async (attachmentId) => {
    try {
      const { data } = await api.delete(`${attachmentBase}/attachments/${attachmentId}`);
      setAttachments(data.attachments || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not remove that file"));
    }
  };

  const handleSendEmail = async () => {
    if (sendingRef.current) return;
    sendingRef.current = true;
    setIsSending(true);
    try {
      // Flush pending edits first — sending a summary that doesn't match what's
      // on screen is worse than not sending, so a failed save aborts the send.
      if (editing) {
        if (!(await handleSaveEdit())) return;
      } else if (instructorNotes !== lesson.instructor_notes) {
        await handleSaveNotes();
      }
      if (isClass) {
        const { data } = await api.post(`/class-lessons/${lessonId}/send-email`);
        const count = data?.sent_count ?? (lesson.member_ids?.length || 0);
        toast.success(`Sent to ${count} member${count === 1 ? "" : "s"}`);
      } else {
        const { data } = await api.post(`/lessons/${lessonId}/send-email`);
        // Name who actually received it: an under-13 summary never reaches the
        // child, and a teen only when the parent opted in. `sent_to` is the
        // band-resolved list the backend used; the mirror is the fallback.
        const sentTo = Array.isArray(data?.sent_to) ? data.sent_to : summaryRecipients(student).map((r) => r.email);
        toast.success(sentTo.length ? `Sent to ${formatRecipientList(sentTo)}` : "Summary sent");
      }
      setLesson({ ...lesson, email_sent: true, email_sent_at: new Date().toISOString() });
    } catch (error) { toast.error(getErrorMessage(error, "Failed")); }
    finally { setIsSending(false); sendingRef.current = false; }
  };

  /**
   * Fetch the exact HTML the family is (or was) sent.
   *
   * Deliberately side-effect free in BOTH directions: the endpoint is a pure
   * read (it never rotates the consent-revoke token or stamps the lesson
   * sent), and this never flushes pending edits — "View the copy that was
   * sent" must show what was sent, not silently save something new first.
   * The top-bar Preview is hidden in edit mode for the same reason: save,
   * then preview.
   */
  const handlePreviewEmail = async () => {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`${lessonEndpoint}/email-preview`);
      setPreview(data);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't build the preview"));
    } finally {
      setPreviewLoading(false);
    }
  };

  /**
   * Email this lesson to the instructor alone. The preview shows the markup;
   * this proves it survives a real mail client, which is the part a dialog
   * can't demonstrate. Writes nothing — the lesson is not marked sent.
   */
  const handleSendTest = async () => {
    setSendingTest(true);
    try {
      const { data } = await api.post(`${lessonEndpoint}/send-test-email`);
      toast.success(`Test sent to ${data?.sent_to || "your inbox"}`);
    } catch (error) {
      toast.error(getErrorMessage(error, "Couldn't send the test email"));
    } finally {
      setSendingTest(false);
    }
  };

  const handleExportPdf = async () => {
    const studentName = student
      ? `${student.first_name} ${student.last_name}`.trim() || "Student"
      : "Student";
    try {
      await exportLessonPdf({ lesson, studentName });
    } catch (_error) {
      toast.error("Failed to export PDF");
    }
  };

  const transcriptText = () => {
    const utts = lesson?.transcript_with_speakers || [];
    if (utts.length > 0) return utts.map((u) => `${u.speaker}: ${u.text}`).join("\n\n");
    return lesson?.transcript || "";
  };

  const hasTranscript = (lesson?.transcript_with_speakers?.length || 0) > 0
    || (lesson?.transcript && lesson.transcript.trim().length > 0);

  const handleDownloadTranscript = () => {
    const text = transcriptText();
    if (!text) return;
    const safe = (lesson?.title || "transcript").replace(/[^a-z0-9_\- ]+/gi, "").trim().replace(/\s+/g, "_") || "transcript";
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${safe}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const formatDuration = (seconds) => {
    const total = Math.max(0, Math.round(Number(seconds) || 0));
    if (total < 60) return `${total}s`;
    const m = Math.floor(total / 60);
    const r = total % 60;
    return r > 0 ? `${m}m ${r}s` : `${m}m`;
  };
  const isProcessing = !lesson?.summary || lesson?.summary === "Processing failed. Please try again.";
  const recordingHold = {
    pending_quota: ["Waiting for recording allowance", "Your recording is saved. Processing will begin when allowance is available."],
    processing_unknown: ["Processing needs review", "Hans is checking the processing result. Retrying this upload will not spend allowance again."],
    verifying: ["Checking your recording", "Hans is checking the audio before processing begins."],
  }[lesson?.processing_status];
  // Re-recording replaces a recorded lesson's audio + summary in place. It is
  // offered only for terminal-state recorded lessons (a completed take you want
  // to redo, or a failed one to retry) — never for typed manual notes (no audio
  // to replace) or while a lesson is still mid-processing. Mirrors the backend's
  // RERECORDABLE_STATUSES.
  const canRerecord =
    !readOnly &&
    !lesson?.manual &&
    (lesson?.processing_status === "complete" || lesson?.processing_status === "failed");

  const handleRerecord = () => {
    const params = new URLSearchParams({ rerecord: lessonId });
    if (lesson.title) params.set("title", lesson.title);
    const base = isClass ? `/hans/class/${lesson.class_id}` : `/hans/${lesson.student_id}`;
    navigate(`${base}?${params.toString()}`);
  };

  if (loading || !lesson) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[var(--accent-yellow-text)]" />
      </div>
    );
  }

  const studentName = student ? `${student.first_name} ${student.last_name}`.trim() : "";

  // Lesson title + meta. Shared so it can render full-width in the processing
  // state, or as the first thing in the reading column once loaded — the latter
  // lets the side rail start at the title line instead of below it.
  const headerBlock = (
    <>
      <ClassTeachingDetails classId={lesson.class_id} />
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {isClass && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-2.5 py-1 text-xs font-medium text-[var(--accent-yellow-text)]" data-testid="class-lesson-badge">
            <Icon name="users" size={13} /> Class lesson
          </span>
        )}
        {lesson.manual && (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-2.5 py-1 text-xs font-medium text-[var(--text-secondary)]" data-testid="typed-notes-badge">
            <Icon name="edit" size={13} /> Typed notes
          </span>
        )}
      </div>
      {/* Authorship, stated rather than implied: what the instructor sees here
          is the same claim the family sees on the email and the PDF. */}
      {isInstructorWritten(lesson) ? (
        <div data-testid="written-by-instructor-eyebrow">
          <Eyebrow icon="edit">Written by you — not an AI summary</Eyebrow>
        </div>
      ) : (
        !isClass && <Eyebrow icon="sparkle" tone="yellow">Summarized by Hans</Eyebrow>
      )}
      {editing && !lesson.title_locked ? (
        <input
          type="text"
          value={titleText}
          maxLength={140}
          onChange={(e) => setTitleText(e.target.value)}
          placeholder="Lesson title"
          aria-label="Lesson title"
          data-testid="edit-title-input"
          className={`${FIELD_CLS} mt-3 font-serif text-[32px] leading-[1.15] tracking-[-0.02em]`}
        />
      ) : (
        <h1 className="mt-3 font-serif text-[46px] font-normal leading-[1.1] tracking-[-0.02em] text-[var(--text-primary)]">{lesson.title}</h1>
      )}
      {editing && lesson.title_locked && (
        <p
          className="mt-2 inline-flex items-center gap-1.5 font-sans text-[13px] text-[var(--text-tertiary)]"
          data-testid="title-locked-note"
        >
          <Icon name="lock" size={14} />
          {lesson.org_name || "The organization"} sets this session&rsquo;s name.
        </p>
      )}
      {/* What the org asked of this session (O7), read beside the write-up.
          The instructor's page only: a family's copy is built elsewhere and
          never carries it. */}
      <SessionBrief session={lesson.session} className="mt-3" />
      <div className="mt-3.5 flex flex-wrap items-center gap-3 font-mono text-[13px] text-[var(--text-tertiary)]">
        {studentName && <><span>{studentName}</span><span className="opacity-40">·</span></>}
        <span>{formatDate(lesson.created_at)}</span>
        {!lesson.manual && <><span className="opacity-40">·</span><span>{formatDuration(lesson.duration_seconds)}</span></>}
      </div>
      {(lesson.program_title || SCHEDULER_VIEW_ENABLED) && (
        <div className="mt-2">
          <ProgramChip lesson={lesson} programTitle={lesson.program_title} orgName={lesson.org_name} accent={lesson.org_accent} />
        </div>
      )}
      {/* Who wasn't there, as the instructor marked it on the day. The names
          are the lesson's own snapshot, not a live roster lookup: a class
          re-imported next term must not rewrite an old session's register.
          Nothing rendered when nobody was marked — an empty register means
          "not taken", which is a different claim from "everyone attended". */}
      {isClass &&
        [
          ["absent", "Absent", "lesson-absences"],
          ["excused", "Excused", "lesson-excused"],
        ].map(([reason, label, testId]) => {
          // A row with no reason predates excused: it is the absence its
          // instructor marked.
          const names = (lesson.absent_students || [])
            .filter((a) => (a.reason || "absent") === reason)
            .map((a) => a.name || "Student");
          return names.length > 0 ? (
            <p key={reason} className="mt-2 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid={testId}>
              <span className="font-medium text-[var(--text-secondary)]">{label}:</span> {names.join(", ")}
            </p>
          ) : null;
        })}
      {substituteLesson ? (
        <p className="mt-2 font-sans text-xs text-[var(--text-tertiary)]" data-testid="substitute-lesson-note">
          Taught by {lesson.substitute.name || lesson.written_by_name || "a substitute"}, substitute. It doesn&rsquo;t count toward
          {readOnly ? " your" : " the class instructor’s"} lessons, and your organization shares it with families.
        </p>
      ) : isClass && !readOnly && (
        <p className="mt-2 font-sans text-xs text-[var(--text-tertiary)]" data-testid="class-edit-note">Edits and sends apply to this lesson’s participants — one shared record.</p>
      )}
    </>
  );

  return (
    <div className="mx-auto max-w-[900px] px-6 pb-16 pt-8 md:px-12" data-testid="lesson-summary">
      {/* Top bar */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {isClass ? (
          <Button variant="bare" icon="chevronLeft" onPress={() => guardedNavigate(lesson.class_id ? `/classes/${lesson.class_id}` : "/students")} data-testid="back-to-class">Class</Button>
        ) : (
          <Button variant="bare" icon="chevronLeft" onPress={() => guardedNavigate(`/students/${lesson.student_id}`)} data-testid="back-to-student">Back</Button>
        )}
        <div className="flex flex-wrap gap-2.5">
          {readOnly ? (
            <Button variant="ghost" icon="users" onPress={() => navigate(lesson.class_id ? `/classes/${lesson.class_id}` : "/students")} data-testid="open-in-class-button">Open in class</Button>
          ) : editing ? (
            <>
              <Button variant="bare" icon="x" onPress={handleCancelEdit} disabled={savingEdit} data-testid="cancel-edit-button">Cancel</Button>
              <Button icon={savingEdit ? undefined : "check"} onPress={handleSaveEdit} disabled={savingEdit} data-testid="save-edit-button">
                {savingEdit && <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />}
                {savingEdit ? "Saving…" : "Save"}
              </Button>
            </>
          ) : (
            <>
              {canRerecord && (
                <Button variant="ghost" icon="mic" onPress={handleRerecord} data-testid="rerecord-lesson-button">Re-record</Button>
              )}
              <Button variant="ghost" icon="edit" onPress={() => setEditing(true)} disabled={isProcessing} data-testid="edit-lesson-button">Edit</Button>
              {!substituteLesson && (
              <Button variant="ghost" icon="eye" onPress={handlePreviewEmail} disabled={isProcessing || previewLoading} data-testid="preview-email-button">
                {previewLoading && <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />}
                Preview email
              </Button>
              )}
              <Button variant="ghost" icon="download" onPress={handleExportPdf} disabled={isProcessing} data-testid="export-pdf-button">Export PDF</Button>
              {!substituteLesson && (
              <Button icon={isSending ? undefined : lesson.email_sent ? "check" : "send"} onPress={handleSendEmail} disabled={isSending || isProcessing} data-testid="send-email-button">
                {isSending && <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />}
                {lesson.email_sent ? "Resend" : isClass ? "Send to Class" : "Send to Student"}
              </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Discard-unsaved-changes guard (mirrors StudentProfile) */}
      <AlertDialog open={!!pendingNav} onOpenChange={(open) => { if (!open) setPendingNav(null); }}>
        <AlertDialogContent className="studio mx-4 max-w-[calc(100%-2rem)] border-[var(--studio-divider)] bg-[var(--studio-overlay)] text-[var(--text-primary)]">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-tertiary)]">You have unsaved edits to this lesson. Leaving now will discard them.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[var(--studio-divider)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { const to = pendingNav; setPendingNav(null); setEditing(false); navigate(to); }}
              className="bg-[var(--state-danger)] text-[var(--text-on-state)]"
              data-testid="discard-lesson-edits-confirm"
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email preview — the actual HTML the family receives, rendered in a
          sandboxed iframe. srcDoc + no allow-scripts: the markup is ours, but
          a preview must never be able to reach into the app that opened it. */}
      <AlertDialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <AlertDialogContent className="studio mx-4 flex max-h-[88vh] max-w-[calc(100%-2rem)] flex-col border-[var(--studio-divider)] bg-[var(--studio-overlay)] text-[var(--text-primary)] sm:max-w-[680px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{lesson?.email_sent ? "The email that was sent" : "Email preview"}</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--text-tertiary)]" data-testid="preview-recipients">
              {preview?.recipients?.length
                ? `${lesson?.email_sent ? "Sent to" : "Will be sent to"} ${preview.recipients.join(", ")}`
                : "No recipient has an email address on file yet."}
              {preview?.copy_to_instructor && (
                <span className="mt-1 block" data-testid="preview-copy-note">
                  A copy also goes to your own inbox.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <iframe
            title="Lesson summary email preview"
            srcDoc={preview?.html || ""}
            sandbox=""
            className={PREVIEW_FRAME_CLS}
            data-testid="preview-email-frame"
          />
          <AlertDialogFooter className="sm:justify-between">
            {/* A rendered dialog can't prove the message survives Gmail or
                Apple Mail — sending yourself the real thing can. */}
            <Button
              variant="ghost"
              icon={sendingTest ? undefined : "send"}
              onPress={handleSendTest}
              disabled={sendingTest}
              data-testid="send-test-email-button"
            >
              {sendingTest && <Loader2 className="mr-0.5 h-4 w-4 animate-spin" />}
              {sendingTest ? "Sending…" : "Send a test to myself"}
            </Button>
            <AlertDialogCancel className="border-[var(--studio-divider)] bg-[var(--studio-interactive)] text-[var(--text-primary)]">
              Close
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!readOnly && <LessonWorkspaceStatus connectionId={lesson?.scheduler_connection_id} appointmentId={lesson?.scheduler_appointment_id} />}
      {isProcessing ? (
        <>
          {headerBlock}
          <Card className="mt-9 text-center" style={{ padding: "56px 20px" }}>
            {!['pending_quota', 'processing_unknown'].includes(lesson?.processing_status) && (
              <Loader2 className="mx-auto mb-6 h-10 w-10 animate-spin text-[var(--accent-yellow-text)]" />
            )}
            <h2 className="mb-2 font-serif text-xl font-normal text-[var(--text-primary)]">{recordingHold?.[0] || "Hans is processing..."}</h2>
            <p className="mb-6 font-sans text-sm text-[var(--text-secondary)]">{recordingHold?.[1] || "Usually takes 1-2 minutes"}</p>
            <Button variant="ghost" icon="refresh" onPress={fetchLessonData}>Check</Button>
          </Card>
        </>
      ) : (
        <>
          {/* Two-row grid: the title + Recipients share row 1 (so Recipients
              sits at the title line), while the reading column + the metadata
              rail share row 2 (so Key elements onward starts at the summary's
              first line, regardless of how the title wraps). */}
          <div className="grid grid-cols-1 items-start gap-x-10 gap-y-9 lg:grid-cols-[1fr_300px]">
            {/* Title + meta — row 1, left */}
            <div className="lg:col-start-1 lg:row-start-1">{headerBlock}</div>

            {/* Reading column — row 2, left */}
            <div className="min-w-0 lg:col-start-1 lg:row-start-2">
              {editing ? (
                <div>
                  <Eyebrow>{sectionLabel("summary", "instructor")}</Eyebrow>
                  <div className="mt-3">
                    <RichTextEditor
                      value={summaryText}
                      onChange={setSummaryText}
                      placeholder="What happened in this lesson?"
                      ariaLabel="Lesson summary"
                      testId="edit-summary-input"
                      minHeight={180}
                      className="font-serif text-[17px] leading-[1.6]"
                    />
                  </div>
                </div>
              ) : (
                <RichText
                  value={lesson.summary}
                  testId="lesson-summary-body"
                  className="font-serif text-[21px] font-normal leading-[1.6] text-[var(--text-primary)]"
                  markerClassName="text-[var(--text-tertiary)]"
                />
              )}

              {editing ? (
                <div className="mt-10">
                  <Eyebrow>{sectionLabel("practice_items", "instructor")}</Eyebrow>
                  <div className="mt-4">
                    <EditableList {...listProps(practiceItems, setPracticeItems, "edit-practice", { numbered: true, placeholder: "Something to practice" })} />
                  </div>
                </div>
              ) : lesson.practice_items?.length > 0 && (
                <div className="mt-10">
                  <Eyebrow>{sectionLabel("practice_items", "instructor")}</Eyebrow>
                  <div className="mt-4 flex flex-col gap-[18px]">
                    {lesson.practice_items.map((item, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] font-mono text-[13px] font-semibold text-[var(--accent-yellow-text)]">{i + 1}</span>
                        <div className="min-w-0 flex-1 font-sans text-[17px] leading-[1.4] text-[var(--text-primary)]">{item}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Student interests & requests — RETIRED, absorbed by Student
                  focus. Same rule as Key elements in the rail: shown only for
                  a lesson that already carries some, and still editable there,
                  so nothing an older lesson holds disappears or becomes
                  uncorrectable. */}
              {lesson.student_requests?.length > 0 && (
                editing ? (
                  <div className="mt-10">
                    <Eyebrow icon="sparkle">{sectionLabel("student_requests", "instructor")}</Eyebrow>
                    <div className="mt-4">
                      <EditableList {...listProps(studentRequests, setStudentRequests, "edit-requests", { placeholder: "An interest or request" })} />
                    </div>
                  </div>
                ) : (
                  <div className="mt-10">
                    <Eyebrow icon="sparkle">{sectionLabel("student_requests", "instructor")}</Eyebrow>
                    <ul className="mt-4 space-y-2">
                      {lesson.student_requests.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 font-sans text-base text-[var(--text-secondary)]"><span className="text-[var(--state-success)]">•</span>{item}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}

              {/* Attachments — instructor-uploaded files. Rendered in the
                  document column (not the sticky rail) because they are content
                  the family receives: they ride along on the summary email and
                  show up in the student portal. Visible in read AND edit mode;
                  the controls, like the rehearsal toggle, live outside the edit
                  toggle because uploads commit on their own. */}
              {(attachments.length > 0 || canAttach) && (
                <div className="mt-10" data-testid="attachments-section">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Eyebrow icon="paperclip">{sectionLabel("attachments", "instructor")}</Eyebrow>
                    {showAttachments && !readOnly && (
                      <span className="font-mono text-[11px] text-[var(--text-tertiary)]" data-testid="attachment-budget">
                        {formatBytes(attachedBytes)} of {formatBytes(MAX_ATTACHMENTS_TOTAL_BYTES)} used
                      </span>
                    )}
                  </div>

                  <div className="mt-4 flex flex-col gap-2">
                    {attachments.map((item) => (
                      <div
                        key={item.attachment_id}
                        className="flex items-center gap-3 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-3"
                        data-testid={`attachment-row-${item.attachment_id}`}
                      >
                        <Icon name="fileText" size={16} className="shrink-0 text-[var(--accent-yellow-text)]" />
                        <a
                          href={item.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download={item.filename}
                          className="min-w-0 flex-1 truncate font-sans text-[15px] text-[var(--text-primary)] hover:underline"
                          data-testid={`attachment-link-${item.attachment_id}`}
                        >
                          {item.filename}
                        </a>
                        <span className="shrink-0 font-mono text-[11px] text-[var(--text-tertiary)]">
                          {formatBytes(item.size_bytes)}
                        </span>
                        {!readOnly && showAttachments && (
                          <button
                            type="button"
                            onClick={() => removeAttachment(item.attachment_id)}
                            aria-label={`Remove ${item.filename}`}
                            className="-mr-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[var(--text-tertiary)] transition-colors hover:text-[var(--state-danger)]"
                            data-testid={`attachment-remove-${item.attachment_id}`}
                          >
                            <Icon name="x" size={16} />
                          </button>
                        )}
                      </div>
                    ))}

                    {uploading && (
                      <div
                        className="flex flex-col gap-2 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-4 py-3"
                        data-testid="attachment-uploading"
                      >
                        <div className="flex items-center gap-3">
                          <Loader2 size={16} className="shrink-0 animate-spin text-[var(--text-tertiary)]" />
                          <span className="min-w-0 flex-1 truncate font-sans text-[15px] text-[var(--text-secondary)]">
                            {uploading.name}
                          </span>
                          <span className="shrink-0 font-mono text-[11px] text-[var(--text-tertiary)]">
                            {uploading.percent}%
                          </span>
                        </div>
                        {/* A 10 MB upload over cellular takes 30s+; an
                            indeterminate spinner alone reads as hung. */}
                        <div className="h-1 overflow-hidden rounded-full bg-[var(--studio-interactive)]">
                          <div
                            className="h-full rounded-full bg-[var(--accent-yellow)] transition-[width]"
                            style={{ width: `${uploading.percent}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {canAttach && (
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        className="hidden"
                        accept={ATTACHMENT_ACCEPT}
                        data-testid="attachment-input"
                        onChange={(e) => uploadAttachment(e.target.files?.[0])}
                      />
                      <Button
                        variant="ghost"
                        onClick={() => attachmentInputRef.current?.click()}
                        disabled={!!uploading || isProcessing}
                        data-testid="attachment-add-button"
                      >
                        <Icon name="paperclip" size={15} />
                        Attach a file
                      </Button>
                      <span className="font-sans text-xs text-[var(--text-tertiary)]">{ATTACHMENT_HINT}</span>
                    </div>
                  )}
                </div>
              )}

              {hasTranscript && (
                <div className="mt-10 rounded-[16px] border border-[var(--studio-divider)] bg-[var(--studio-raised)] p-5" data-testid="transcript-section">
                  <div className="flex items-center justify-between gap-3">
                    <button type="button" onClick={() => setTranscriptOpen((o) => !o)} className="flex flex-1 items-center gap-2 text-left" data-testid="transcript-toggle" aria-expanded={transcriptOpen}>
                      <Icon name="list" size={16} className="text-[var(--text-tertiary)]" />
                      <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Transcript</span>
                      <Icon name={transcriptOpen ? "chevronUp" : "chevronDown"} size={16} className="text-[var(--text-tertiary)]" />
                    </button>
                    <button type="button" onClick={handleDownloadTranscript} className="flex items-center gap-1.5 rounded-full border border-[var(--studio-divider)] px-3 py-1 font-sans text-xs text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]" data-testid="download-transcript-button">
                      <Icon name="download" size={13} /> .txt
                    </button>
                  </div>
                  {transcriptOpen && (
                    <div className="mt-4" data-testid="transcript-body">
                      <p className="mb-4 font-sans text-xs italic text-[var(--text-tertiary)]">Auto-transcribed by Hans — speaker labels and wording may contain errors.</p>
                      <div className="max-h-[480px] space-y-3 overflow-y-auto pr-2">
                        {(lesson.transcript_with_speakers?.length || 0) > 0 ? (
                          lesson.transcript_with_speakers.map((u, i) => (
                            <div key={i} className="text-sm leading-relaxed">
                              <span className={u.speaker === "Teacher" ? "font-medium text-[var(--accent-yellow-text)]" : "font-medium text-[var(--state-success)]"}>{u.speaker}:</span>{" "}
                              <span className="text-[var(--text-secondary)]">{u.text}</span>
                            </div>
                          ))
                        ) : (
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-[var(--text-secondary)]">{lesson.transcript}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Recipients — row 1, right (in line with the title) on lg; on
                mobile, order it first so it sits directly under the Export /
                Send buttons instead of after the transcript. */}
            {!isClass && student && (() => {
              // Follows the age-band rules the send uses, so the card never
              // names an address the summary will not reach.
              const band = student.age_band;
              const minor = band === "under_13" || band === "13_17";
              const recipients = summaryRecipients(student);
              const studentAddress = recipients.find((r) => r.role === "student")?.email;
              const otherAddress = recipients.find((r) => r.role !== "student")?.email;
              const studentEligible = !minor || (band === "13_17" && student.cc_teen);
              return (
              <div data-testid="recipients-card" className="order-first lg:order-none lg:col-start-2 lg:row-start-1">
                <Eyebrow>Recipients</Eyebrow>
                  <div className="mt-3 flex flex-col gap-3">
                    <div>
                      <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">Student</p>
                      <p className="mt-1 font-sans text-sm font-medium text-[var(--text-primary)]" data-testid="recipient-student-name">
                        {studentName || "—"}
                      </p>
                      {studentAddress ? (
                        <p className="mt-0.5 break-all font-mono text-[13px] text-[var(--text-secondary)]" data-testid="recipient-student-email">
                          {studentAddress}
                        </p>
                      ) : studentEligible ? (
                        <p className="mt-0.5 font-sans text-[13px] italic text-[var(--text-tertiary)]">No email on file</p>
                      ) : (
                        <p className="mt-0.5 font-sans text-[13px] italic text-[var(--text-tertiary)]" data-testid="recipient-student-excluded">
                          {STUDENT_EXCLUDED_NOTE[band]}
                        </p>
                      )}
                    </div>
                    {(minor || otherAddress) && (
                      <div className="border-t border-[var(--studio-divider)] pt-3">
                        <p className="font-mono text-[11px] uppercase tracking-wide text-[var(--text-tertiary)]">
                          {minor ? "Guardian" : "Also sent to"}
                        </p>
                        {student.parent_name && (
                          <p className="mt-1 font-sans text-sm font-medium text-[var(--text-primary)]" data-testid="recipient-guardian-name">
                            {student.parent_name}
                          </p>
                        )}
                        {otherAddress ? (
                          <p className="mt-0.5 break-all font-mono text-[13px] text-[var(--text-secondary)]" data-testid="recipient-guardian-email">
                            {otherAddress}
                          </p>
                        ) : minor ? (
                          <p className="mt-0.5 font-sans text-[13px] italic text-[var(--state-warning)]" data-testid="recipient-guardian-missing">
                            No parent email on file — add one to send this summary
                          </p>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Metadata rail — row 2, right (starts at the summary line) */}
            <div className="flex flex-col gap-6 lg:col-start-2 lg:row-start-2 lg:sticky lg:top-8">
              {editing ? (
                <div>
                  <Eyebrow>{sectionLabel("student_focus", "instructor")}</Eyebrow>
                  <div className="mt-3">
                    <EditableList {...listProps(studentFocus, setStudentFocus, "edit-student-focus", { numbered: true, compact: true, placeholder: "What they're working toward" })} />
                  </div>
                </div>
              ) : lesson.student_focus?.length > 0 && (
                <div data-testid="student-focus-rail">
                  <Eyebrow>{sectionLabel("student_focus", "instructor")}</Eyebrow>
                  <div className="mt-3 flex flex-col gap-2.5">
                    {lesson.student_focus.map((k, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <span className="font-mono text-xs text-[var(--accent-yellow-text)]">{i + 1}</span>
                        <span className="font-sans text-[13px] text-[var(--text-secondary)]">{k}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key elements — RETIRED, replaced by Student focus. Shown ONLY
                  for a lesson that already carries some, so an older lesson
                  keeps everything it always showed while a new one is never
                  offered a section nothing will ever fill. Still editable for
                  the same reason: an instructor reading an old lesson must be
                  able to correct what it actually shows. */}
              {lesson.key_elements?.length > 0 && (
                editing ? (
                  <div>
                    <Eyebrow>{sectionLabel("key_elements", "instructor")}</Eyebrow>
                    <div className="mt-3">
                      <EditableList {...listProps(keyElements, setKeyElements, "edit-key-elements", { numbered: true, compact: true, placeholder: "Key element" })} />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Eyebrow>{sectionLabel("key_elements", "instructor")}</Eyebrow>
                    <div className="mt-3 flex flex-col gap-2.5">
                      {lesson.key_elements.map((k, i) => (
                        <div key={i} className="flex items-center gap-2.5">
                          <span className="font-mono text-xs text-[var(--accent-yellow-text)]">{i + 1}</span>
                          <span className="font-sans text-[13px] text-[var(--text-secondary)]">{k}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              )}

              {editing ? (
                <div>
                  <Eyebrow>{sectionLabel("core_concepts", "instructor")}</Eyebrow>
                  <div className="mt-3">
                    <EditableList {...listProps(coreConcepts, setCoreConcepts, "edit-core-concepts", { compact: true, placeholder: "Concept" })} />
                  </div>
                </div>
              ) : lesson.core_concepts?.length > 0 && (
                <div>
                  <Eyebrow>{sectionLabel("core_concepts", "instructor")}</Eyebrow>
                  <div className="mt-3 flex flex-wrap gap-1.5">{lesson.core_concepts.map((c) => <Tag key={c}>{c}</Tag>)}</div>
                </div>
              )}

              {/* Demonstration clips — what the student will hear in their
                  portal, shown here so the instructor sees exactly what was
                  kept. Renders nothing when the lesson has none. */}
              {!editing && (
                <LessonClips
                  client={api}
                  audience="instructor"
                  path={isClass ? `/lessons/${lessonId}/clips?kind=class` : `/lessons/${lessonId}/clips`}
                />
              )}

              {!readOnly && (
                <Card padding={16}>
                  <Eyebrow>{sectionLabel("instructor_notes", "instructor")}</Eyebrow>
                  <div className="mt-3">
                    <RichTextEditor
                      value={instructorNotes}
                      onChange={setInstructorNotes}
                      placeholder={isClass ? "Add notes for the class..." : "Add notes for the student..."}
                      ariaLabel="Instructor notes"
                      testId="instructor-notes-input"
                      minHeight={100}
                      className="text-base"
                    />
                  </div>
                  {/* In edit mode the top-bar Save persists notes along with the
                      rest of the lesson — a second Save here would just be a
                      partial write of what's already staged. */}
                  {!editing && (
                    <Button variant="ghost" fullWidth onPress={handleSaveNotes} disabled={isSaving} className="mt-3" data-testid="save-notes-button">
                      {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save
                    </Button>
                  )}

                  {!isClass && showRehearsalToggle && (
                    <div className="mt-4 border-t border-[var(--studio-divider)] pt-4">
                      <button
                        onClick={async () => {
                          const newVal = !rehearsalAssigned;
                          setSavingToggle(true);
                          try {
                            await api.put(`/lessons/${lessonId}`, { rehearsal_assigned: newVal });
                            setRehearsalAssigned(newVal);
                            toast.success(newVal ? "Rehearsal assigned" : "Rehearsal unassigned");
                          } catch (_err) { toast.error("Failed to update"); }
                          finally { setSavingToggle(false); }
                        }}
                        disabled={savingToggle}
                        className="group flex w-full items-center justify-between"
                      >
                        <div className="text-left">
                          <p className="font-sans text-sm font-medium text-[var(--text-secondary)]">Assign Rehearsal</p>
                          <p className="mt-0.5 font-sans text-xs text-[var(--text-tertiary)]">Student can submit a practice recording for this lesson</p>
                        </div>
                        {savingToggle ? (
                          <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[var(--text-tertiary)]" />
                        ) : rehearsalAssigned ? (
                          <ToggleRight className="h-8 w-8 shrink-0 text-[var(--accent-yellow-text)]" />
                        ) : (
                          <ToggleLeft className="h-8 w-8 shrink-0 text-[var(--text-tertiary)]" />
                        )}
                      </button>
                    </div>
                  )}
                </Card>
              )}

              {/* Authorship is a claim made to a family, so the instructor
                  makes it deliberately. Typed lessons arrive pre-ticked; a
                  recorded lesson whose summary was rewritten can be signed. */}
              {editing && (
                <Card padding={16}>
                  <Eyebrow>Authorship</Eyebrow>
                  <label className="mt-3 flex cursor-pointer items-start gap-2.5">
                    <input
                      type="checkbox"
                      checked={instructorWritten}
                      onChange={(e) => setInstructorWritten(e.target.checked)}
                      className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--accent-yellow)]"
                      data-testid="instructor-written-toggle"
                    />
                    <span className="font-sans text-sm text-[var(--text-secondary)]">
                      These notes were written by me
                      <span className="mt-0.5 block font-sans text-xs text-[var(--text-tertiary)]">
                        The email and PDF will say so instead of crediting Hans.
                      </span>
                    </span>
                  </label>
                </Card>
              )}

              {lesson.email_sent && (
                <div className="rounded-[16px] border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] p-4" data-testid="email-sent-card">
                  <div className="flex items-center gap-2 text-[var(--accent-yellow-text)]">
                    <CheckCircle2 className="h-5 w-5" /><span className="font-sans text-sm font-medium">Email sent</span>
                  </div>
                  {lesson.email_sent_at && (
                    <p className="mt-1.5 font-mono text-[12px] text-[var(--text-tertiary)]" data-testid="email-sent-at">
                      {new Date(lesson.email_sent_at).toLocaleString("en-US", {
                        month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
                      })}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handlePreviewEmail}
                    className="mt-2 font-sans text-xs text-[var(--accent-yellow-text)] underline-offset-2 hover:underline"
                    data-testid="view-sent-copy-button"
                  >
                    View the copy that was sent
                  </button>
                </div>
              )}
            </div>
          </div>

        </>
      )}

      {/* Conversation (F9). Below the lesson, because it is a reply TO the
          work above rather than part of it.
          Self-hides when COMMENTS_ENABLED is off; the readOnly view (a class
          lesson reached from one member's history) gets no composer, since
          that page is a copy of somebody else's thread. */}
      {!readOnly && (
        <CommentThread
          client={api}
          basePath="/comments"
          artifactKind={isClass ? "class_lesson" : "lesson"}
          artifactId={lessonId}
        />
      )}
    </div>
  );
};

export default LessonSummary;
